import { CalendarClock, Clock, Eye, Globe, Link, Lock, Plus, Trophy, Video } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react'
import { Button } from '@/components/ui/Button'
import { Segmented, type SegmentedOption } from '@/components/ui/Segmented'
import { Pagination } from '@/components/data-display/Pagination'
import { EmptyState } from '@/components/ds/EmptyState'
import { useActiveChannel } from '@/hooks/useActiveChannel'
import {
  useVideos,
  type VideoItem,
  type VideosScope,
  type VideosSort,
  type VideosStatus,
  type VideosVisibility,
} from '@/api/endpoints/videos'
import { usePools } from '@/api/endpoints/pools'
import { formatScheduledLabel, formatScheduledTooltip } from '@/lib/format'
import { forwardChannel } from '@/lib/routing'
import { ProjectCard } from '@/features/projects/components/ProjectCard'
import { cardDateInfo } from '@/features/projects/cardDateInfo'
import { useScrollToTopAfterLoad } from '@/features/projects/scrollToTop'
import { SelectionBar, type PageSelectionState } from '@/features/projects/components/SelectionBar'
import { PoolChannelsMenu } from '@/features/projects/components/PoolChannelsMenu'
import { ChannelActionsMenu } from '@/features/projects/components/ChannelActionsMenu'
import { CheckboxFilterMenu, type CheckboxFilterMenuOption } from '@/components/ui/CheckboxFilterMenu'
import {
  applyRange,
  canChangeVisibility,
  deselectAll,
  isSelectable,
  selectAll,
  selectionKey,
  toggleOne,
  withoutKeys,
  type SelectedVideo,
} from '@/features/projects/selection'
import {
  hiddenChannels,
  hiddenVisibility,
  isVisibility,
  nextProjectsSearch,
  originBadge,
  rememberFilters,
  statusOptions,
  type ProjectsSearch,
} from '@/features/projects/filters'
import { channelColor } from '@/lib/channelColor'
import { thumbnailUrl } from '@/api/endpoints/media'
import styles from './ProjectsPage.module.scss'
import { QueryErrorNotice } from '@/components/feedback/QueryErrorNotice'

const PER_PAGE = 24

export type SortKey = VideosSort

const SORT_OPTIONS: ReadonlyArray<SegmentedOption<SortKey>> = [
  { value: 'recent', label: 'Mais recentes', icon: <Clock /> },
  { value: 'ranking', label: 'Por ranking', icon: <Trophy /> },
  { value: 'views', label: 'Mais vistos', icon: <Eye /> },
]

export type { ProjectsSearch }

const SCOPE_OPTIONS: ReadonlyArray<SegmentedOption<VideosScope>> = [
  { value: 'channel', label: 'Canal ativo' },
  { value: 'pool', label: 'Pool do canal' },
  { value: 'all', label: 'Todos os canais' },
]

// O subfiltro de "Publicados": um balde por visibilidade, com o ícone do chip.
const VISIBILITY_OPTIONS: readonly (CheckboxFilterMenuOption & { value: VideosVisibility })[] = [
  { value: 'public', label: 'Público', icon: <Globe /> },
  { value: 'unlisted', label: 'Não listado', icon: <Link /> },
  { value: 'private', label: 'Privado', icon: <Lock /> },
  { value: 'scheduled', label: 'Agendado', icon: <CalendarClock /> },
]

export interface ProjectsPageProps {
  search: ProjectsSearch
}

export function ProjectsPage({ search }: ProjectsPageProps) {
  const navigate = useNavigate()
  const { active, channels, isLoading: channelsLoading } = useActiveChannel(search.channel ?? null)
  const channelSlug = active?.slug ?? ''
  const sort = search.sort ?? 'recent'
  const page = search.page ?? 1
  const status: VideosStatus = search.status ?? 'all'
  // O pool do canal ativo, se ele tira vídeos de um. Sem pool, o filtro some
  // e um "pool" lembrado de outro canal vale como o canal ativo.
  const { data: pools } = usePools()
  const activePool = pools?.find((p) => p.members.some((m) => m.slug === channelSlug))
  const scope: VideosScope = search.scope === 'pool' && pools && !activePool ? 'channel' : (search.scope ?? 'channel')
  const hidden = useMemo(() => hiddenChannels(search), [search])
  const hiddenVis = useMemo(() => (status === 'published' ? hiddenVisibility(search) : []), [search, status])
  // Os canais do menu do pool: os membros e o próprio pool, que guarda os
  // vídeos ainda livres. Um slug desmarcado que saiu do pool não conta.
  const poolOptions = useMemo(
    () =>
      activePool
        ? [
            ...activePool.members.map((m) => ({ slug: m.slug, label: m.name })),
            { slug: activePool.slug, label: 'Livres no pool' },
          ]
        : [],
    [activePool],
  )
  const exclude = useMemo(
    () => hidden.filter((slug) => poolOptions.some((o) => o.slug === slug)),
    [hidden, poolOptions],
  )
  const scopeOptions: ReadonlyArray<SegmentedOption<VideosScope>> = activePool
    ? SCOPE_OPTIONS.map((o) =>
        o.value === 'pool'
          ? {
              ...o,
              label: exclude.length > 0 ? `Pool do canal · ${poolOptions.length - exclude.length}/${poolOptions.length}` : o.label,
              wrap: (item) => (
                <PoolChannelsMenu
                  poolName={activePool.name}
                  options={poolOptions}
                  hidden={exclude}
                  onHiddenChange={(next) => {
                    go({ scope: 'pool', hide: next })
                  }}
                >
                  {item}
                </PoolChannelsMenu>
              ),
            }
          : o,
      )
    : SCOPE_OPTIONS.filter((o) => o.value !== 'pool')

  const { data, isPending, isPlaceholderData, isError, error, refetch } = useVideos({
    channel: channelSlug,
    page,
    limit: PER_PAGE,
    sort,
    status,
    scope,
    exclude,
    hideVisibility: hiddenVis,
  })

  // Backend already orders by `sort` — no client-side re-sort.
  const videos: VideoItem[] = useMemo(() => data?.videos ?? [], [data])
  const totalItems = data?.total ?? 0
  // Backend novo manda a chave youtube_privacy no upload; o antigo, não.
  const visibilitySupported = useMemo(() => videos.some((v) => canChangeVisibility(v.upload)), [videos])
  const totalPages = data?.pages ?? 1
  // Backend novo ecoa a chave hide_visibility; contra o antigo, o menu não aparece.
  const visibilityFilterSupported = data != null && 'hide_visibility' in data
  const visibilityFiltered = visibilityFilterSupported && hiddenVis.length > 0
  const publishOptions: ReadonlyArray<SegmentedOption<VideosStatus>> = statusOptions(scope).map((o) =>
    o.value === 'published' && visibilityFilterSupported
      ? {
          ...o,
          // Curto em qualquer escopo, para caber no celular.
          label: visibilityFiltered
            ? `Publicados · ${VISIBILITY_OPTIONS.length - hiddenVis.length}/${VISIBILITY_OPTIONS.length}`
            : o.label,
          wrap: (item) => (
            <CheckboxFilterMenu
              title={(shown, total) => `Visibilidade · ${shown} de ${total}`}
              options={VISIBILITY_OPTIONS}
              hidden={hiddenVis}
              onHiddenChange={(next) => {
                go({ status: 'published', vis: next.filter(isVisibility) })
              }}
            >
              {item}
            </CheckboxFilterMenu>
          ),
        }
      : o,
  )
  // Skeleton no primeiro carregamento e em toda troca de lista (canal, página,
  // filtro): ``keepPreviousData`` deixa a lista anterior na tela como
  // placeholder, e ela parecia ser a nova até a resposta chegar. Na troca, o
  // skeleton tem a quantidade de cards que estava na tela, para a altura não
  // pular. Refetch em segundo plano não é troca e não mostra skeleton.
  const isLoadingGrid = isPending || isPlaceholderData
  const skeletonCount = isPlaceholderData && videos.length > 0 ? videos.length : PER_PAGE
  const pageRef = useRef<HTMLDivElement>(null)
  // A lista troca com qualquer parâmetro dela; o refetch em segundo plano não.
  const listKey = [channelSlug, page, sort, status, scope, exclude.join(','), hiddenVis.join(',')].join('|')
  useScrollToTopAfterLoad(pageRef, listKey, isLoadingGrid)

  // ---- Multi-select ------------------------------------------------------
  // The card's bottom bar toggles it; the poster keeps opening the project.
  // It survives page and sort changes (bulk actions carry a snapshot of each
  // video) but not a channel switch.
  const [selection, setSelection] = useState<ReadonlyMap<string, SelectedVideo>>(() => new Map())
  // A seleção vale para um canal (ou para a visão de todos), não sobrevive à troca.
  const selectionScope = scope === 'all' ? '*' : `${scope}:${channelSlug}`
  const [selectionChannel, setSelectionChannel] = useState(selectionScope)
  if (selectionChannel !== selectionScope) {
    setSelectionChannel(selectionScope)
    setSelection(new Map())
  }
  // Last card toggled — the start of a shift-click range.
  const anchorRef = useRef<string | null>(null)

  // Vídeo livre do pool também entra: a seleção só baixa e arquiva, e o
  // arquivar apaga do pool (o DELETE aceita o slug do pool do dono). Com o
  // skeleton na tela, a página é a anterior: "Selecionar página" pegaria cards
  // que já não aparecem.
  const pageVideos = useMemo(
    () => (isLoadingGrid ? [] : videos.map((v) => toSelectedVideo(v, channelSlug))),
    [isLoadingGrid, videos, channelSlug],
  )
  const selectableByKey = useMemo(() => new Map(pageVideos.map((v) => [selectionKey(v), v] as const)), [pageVideos])
  const pageSelectable = pageVideos.filter((v) => isSelectable(v.status))
  const selectedOnPage = pageSelectable.filter((v) => selection.has(selectionKey(v))).length
  const pageState: PageSelectionState =
    selectedOnPage === 0 ? 'none' : selectedOnPage === pageSelectable.length ? 'all' : 'some'
  // The grid refetches in the background — prefer this page's fresh copy of
  // a selected video over the snapshot, and drop any that started generating.
  const selectedVideos = useMemo(() => {
    const fresh = new Map(pageVideos.map((v) => [selectionKey(v), v]))
    return [...selection.entries()]
      .map(([key, v]) => fresh.get(key) ?? v)
      .filter((v) => isSelectable(v.status))
  }, [selection, pageVideos])

  function toggleSelect(video: SelectedVideo, event: MouseEvent<HTMLButtonElement>) {
    const key = selectionKey(video)
    const anchor = anchorRef.current
    if (event.shiftKey && anchor && anchor !== key) {
      const select = !selection.has(key)
      setSelection((prev) => applyRange(prev, pageVideos, anchor, video, select))
    } else {
      setSelection((prev) => toggleOne(prev, video))
    }
    anchorRef.current = key
  }

  const clearSelection = useCallback(() => {
    setSelection(new Map())
    anchorRef.current = null
  }, [])

  // Archiving the whole last page leaves `page` past the end, and the grid
  // would claim the channel is empty. Step back to the new last page.
  useEffect(() => {
    if (data && data.pages >= 1 && page > data.pages) {
      void navigate({
        to: '/',
        search: (prev) => nextProjectsSearch(prev, { page: data.pages }),
      })
    }
  }, [data, page, navigate])

  // Os filtros vão para a URL e para o localStorage no mesmo passo: gravar
  // depois, num efeito, deixaria o beforeLoad da rota restaurar a escolha
  // anterior ao voltar um filtro para o default.
  function go(patch: Parameters<typeof nextProjectsSearch>[1]) {
    const next = nextProjectsSearch(search, patch)
    rememberFilters(next)
    void navigate({ to: '/', search: next })
  }

  function setSort(next: SortKey) {
    // Reset page when sort changes — ranking vs recent land on different rows.
    go({ sort: next })
  }

  function setStatus(next: VideosStatus) {
    go({ status: next })
  }

  function setScope(next: VideosScope) {
    go({ scope: next })
  }

  function setPage(next: number) {
    go({ page: next })
  }

  // Empty state: channels exist but no videos (or no channel yet)
  if (!channelsLoading && channels.length === 0) {
    return (
      <EmptyState
        icon={<Video />}
        title="Nenhum canal configurado"
        description="Crie um canal para começar a gerar vídeos."
        action={<Button leadingIcon={<Plus />}>Criar canal</Button>}
      />
    )
  }

  return (
    <div ref={pageRef} className={styles.page}>
      <header className={styles.toolbar}>
        <h2 className={styles.heading}>Vídeos</h2>
        <div className={styles.spacer} />
        <Segmented<VideosScope> options={scopeOptions} value={scope} onValueChange={setScope} aria-label="Quais canais" />
        <Segmented<VideosStatus>
          options={publishOptions}
          value={status}
          onValueChange={setStatus}
          aria-label="Publicação"
        />
        <Segmented<SortKey>
          options={SORT_OPTIONS}
          value={sort}
          onValueChange={setSort}
          aria-label="Ordenação"
        />
        <Button
          leadingIcon={<Plus />}
          onClick={() => {
            void navigate({ to: '/factory', search: forwardChannel })
          }}
        >
          Gerar vídeo
        </Button>
        {active ? (
          <ChannelActionsMenu
            channelName={active.name}
            onAction={() => {
              void navigate({
                to: '/channels/$slug/recovery',
                params: { slug: active.slug },
                search: { channel: active.slug },
              })
            }}
          />
        ) : null}
      </header>

      {isError ? (
        <QueryErrorNotice
          error={error}
          label="os vídeos"
          onRetry={() => {
            void refetch()
          }}
        />
      ) : isLoadingGrid ? (
        <>
          <div className={styles.grid} aria-busy>
            {Array.from({ length: skeletonCount }).map((_, i) => (
              <div key={i} className={styles.skeleton} />
            ))}
          </div>
          {/* Keep pagination mounted during refetch so the user still sees
              the page range + total count while a new page loads. */}
          {totalPages > 1 ? (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              totalItems={totalItems}
              perPage={PER_PAGE}
              onPageChange={setPage}
            />
          ) : null}
        </>
      ) : videos.length === 0 ? (
        status !== 'all' ? (
          <EmptyState
            icon={<Video />}
            title={
              status === 'published'
                ? visibilityFiltered
                  ? 'Nenhum vídeo publicado com essa visibilidade'
                  : 'Nenhum vídeo publicado'
                : 'Nenhum vídeo não publicado'
            }
            description="Troque o filtro de publicação para ver os outros."
          />
        ) : (
          <EmptyState
            icon={<Video />}
            title="Nenhum vídeo ainda"
            description={
              scope === 'all'
                ? 'Nenhum canal tem vídeo ainda. Gere o primeiro para começar.'
                : scope === 'pool'
                  ? `O pool ${activePool?.name ?? ''} ainda não gerou vídeo.`
                  : `O canal ${active?.name ?? ''} está vazio. Gere o primeiro vídeo para começar.`
            }
            action={<Button leadingIcon={<Plus />}>Gerar primeiro vídeo</Button>}
          />
        )
      ) : (
        <>
          <div className={styles.grid}>
            {videos.map((v) => {
              const item = selectableByKey.get(selectionKey(v))
              const selectable = item != null && isSelectable(item.status)
              return (
                <ProjectCard
                  key={`${v.id}-${v.language}`}
                  videoId={v.id}
                  language={v.language}
                  title={videoTitle(v)}
                  status={v.ui_status}
                  posterHex={channelColor(v.channel)}
                  thumbnailUrl={v.thumbnail_path ? thumbnailUrl(v.thumbnail_path, 360) : null}
                  durationSec={v.duration_sec ?? null}
                  views={v.youtube_stats?.views ?? null}
                  likes={v.youtube_stats?.likes ?? null}
                  comments={v.youtube_stats?.comments ?? null}
                  dateInfo={cardDateInfo(v)}
                  privacy={v.upload?.youtube_privacy ?? null}
                  stepLabel={mapStepLabel(v.status)}
                  scheduledLabel={formatScheduledLabel(v.upload?.scheduled_for)}
                  scheduledTooltip={formatScheduledTooltip(v.upload?.scheduled_for)}
                  score={v.ranking?.final_score ?? null}
                  origin={originBadge(v, scope, channelSlug)}
                  selected={selectable && selection.has(selectionKey(item))}
                  onToggleSelect={
                    selectable
                      ? (event) => {
                          toggleSelect(item, event)
                        }
                      : undefined
                  }
                />
              )
            })}
          </div>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={totalItems}
            perPage={PER_PAGE}
            onPageChange={setPage}
          />
        </>
      )}

      {selectedVideos.length > 0 ? (
        <SelectionBar
          selected={selectedVideos}
          visibilitySupported={visibilitySupported}
          pageState={pageState}
          canSelectPage={pageSelectable.length > 0}
          onSelectPage={() => {
            setSelection((prev) => selectAll(prev, pageVideos))
          }}
          onDeselectPage={() => {
            setSelection((prev) => deselectAll(prev, pageVideos))
          }}
          onClear={clearSelection}
          onArchived={(keys) => {
            setSelection((prev) => withoutKeys(prev, keys))
          }}
        />
      ) : null}
    </div>
  )
}

/** Same fallback chain the card shows: generated title → edited title → id. */
function videoTitle(v: VideoItem): string {
  return v.metadata?.title || v.meta_title || v.id
}

function toSelectedVideo(v: VideoItem, activeChannel: string): SelectedVideo {
  return {
    id: v.id,
    language: v.language,
    // Disk-only projects can come without a channel; the grid only ever
    // lists the active one, and the archive DELETE needs the right slug —
    // it resolves the folder to remove from it.
    channel: v.channel || activeChannel,
    title: videoTitle(v),
    status: v.ui_status,
    videoPath: v.video_path,
    views: v.youtube_stats?.views ?? null,
    youtubeId: v.upload?.youtube_video_id || v.youtube_video_id || "",
    youtubePrivacy: v.upload?.youtube_privacy ?? null,
  }
}

function mapStepLabel(status: string): string | null {
  const STEPS: Record<string, string> = {
    generating_script: 'Passo 1/7 · Roteiro',
    generating_audio: 'Passo 2/7 · Áudio',
    fetching_media: 'Passo 3/7 · Vídeos',
    composing: 'Passo 4/7 · Cortes',
    generating_metadata: 'Passo 5/7 · Metadata',
  }
  return STEPS[status] ?? null
}
